import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface SearchItem {
  label: string;
  description: string;
  path: string;
  icon: string;
  keywords: string[];
}

const MANAGEMENT_ITEMS: SearchItem[] = [
  { label: 'Dashboard', description: 'Brokerage admin overview', path: '/portal/admin', icon: 'ri-dashboard-line', keywords: ['home', 'overview', 'stats'] },
  { label: 'Listings', description: 'Manage homes for sale', path: '/portal/admin/listings', icon: 'ri-building-line', keywords: ['properties', 'homes', 'sale', 'mls'] },
  { label: 'Leads', description: 'CRM & lead pipeline', path: '/portal/admin/leads', icon: 'ri-user-search-line', keywords: ['prospects', 'inquiries', 'crm', 'buyers', 'sellers'] },
  { label: 'Offers', description: 'Buyer offers on listings', path: '/portal/admin/offers', icon: 'ri-hand-coin-line', keywords: ['bids', 'purchase', 'negotiation'] },
  { label: 'Home Valuations', description: 'AI home valuation requests', path: '/portal/admin/valuations', icon: 'ri-line-chart-line', keywords: ['estimate', 'value', 'comps', 'price'] },
  { label: 'Reimagine Jobs', description: 'AI renovation render requests', path: '/portal/admin/reimagine', icon: 'ri-magic-line', keywords: ['renovation', 'visualize', 'remodel', 'ai image'] },
  { label: 'Agents', description: 'Manage agent profiles', path: '/portal/admin/agents', icon: 'ri-team-line', keywords: ['team', 'realtors', 'staff', 'bios'] },
  { label: 'Reviews', description: 'Client testimonials', path: '/portal/admin/reviews', icon: 'ri-star-line', keywords: ['testimonials', 'ratings', 'feedback'] },
  { label: 'Neighborhoods', description: 'Neighborhood guides', path: '/portal/admin/neighborhoods', icon: 'ri-map-pin-line', keywords: ['areas', 'markets', 'guides', 'communities'] },
];

export default function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const filteredItems = useMemo(() => {
    if (!query.trim()) return MANAGEMENT_ITEMS.slice(0, 10);
    const q = query.toLowerCase();
    return MANAGEMENT_ITEMS.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.keywords.some((k) => k.includes(q)),
    );
  }, [query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredItems.length]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => {
          if (prev) return prev;
          setQuery('');
          setSelectedIndex(0);
          return true;
        });
        return;
      }
      if (!isOpen) return;

      if (e.key === 'Escape') {
        setIsOpen(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % Math.max(filteredItems.length, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % Math.max(filteredItems.length, 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          navigate(filteredItems[selectedIndex].path);
          setIsOpen(false);
        }
      }
    },
    [isOpen, filteredItems, selectedIndex, navigate],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleSelect = (item: SearchItem) => {
    navigate(item.path);
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsOpen(false)}></div>
      <div className="relative w-full max-w-xl mx-4 bg-white dark:bg-dark-800 rounded-2xl shadow-2xl overflow-hidden border border-warm-200 dark:border-dark-700">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-warm-100 dark:border-dark-700">
          <i className="ri-search-line text-dark-400 dark:text-dark-300 text-lg"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, modules, features..."
            className="flex-1 bg-transparent text-dark-900 dark:text-white text-sm outline-none placeholder-dark-400 dark:placeholder-dark-400"
            autoFocus
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-dark-400 dark:text-dark-300 bg-warm-50 dark:bg-dark-700 rounded-md border border-warm-200 dark:border-dark-600">
            <span>ESC</span>
          </kbd>
        </div>
        <div className="max-h-72 overflow-y-auto py-2">
          {filteredItems.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <div className="w-10 h-10 bg-warm-100 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-search-line text-dark-400"></i>
              </div>
              <p className="text-dark-500 text-sm">No results found</p>
              <p className="text-dark-400 text-xs mt-1">Try a different search term</p>
            </div>
          ) : (
            filteredItems.map((item, index) => (
              <button
                key={item.path}
                onClick={() => handleSelect(item)}
                className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                  index === selectedIndex
                    ? 'bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400'
                    : 'text-dark-700 dark:text-dark-200 hover:bg-warm-50 dark:hover:bg-dark-700'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    index === selectedIndex
                      ? 'bg-brand-500 text-white'
                      : 'bg-warm-100 dark:bg-dark-700 text-dark-500 dark:text-dark-300'
                  }`}
                >
                  <i className={`${item.icon} text-sm`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{item.label}</p>
                  <p className="text-xs opacity-70 truncate">{item.description}</p>
                </div>
                {index === selectedIndex && (
                  <i className="ri-arrow-right-line text-brand-500 ml-auto"></i>
                )}
              </button>
            ))
          )}
        </div>
        <div className="px-5 py-2.5 border-t border-warm-100 dark:border-dark-700 flex items-center gap-4 text-[11px] text-dark-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-warm-100 dark:bg-dark-700 rounded text-[10px] border border-warm-200 dark:border-dark-600">↑↓</kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-warm-100 dark:bg-dark-700 rounded text-[10px] border border-warm-200 dark:border-dark-600">↵</kbd>
            Select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-warm-100 dark:bg-dark-700 rounded text-[10px] border border-warm-200 dark:border-dark-600">Esc</kbd>
            Close
          </span>
        </div>
      </div>
    </div>
  );
}